/**
 * Beta Registration Page
 * Handles beta invitation token verification and new user registration
 * Supports: Google, Microsoft, and Email/Password registration
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Loader2, CheckCircle, AlertCircle, Eye, EyeOff, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Microsoft Logo SVG Component
const MicrosoftLogo = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
    <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
  </svg>
);

// Google Logo SVG Component
const GoogleLogo = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

interface InvitationData {
  id: string;
  email: string;
  name?: string;
  company?: string;
  trial_days: number;
  expires_at: string;
}

const BetaRegister: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    password: '',
    confirmPassword: ''
  });
  
  useEffect(() => {
    if (!token) {
      setError('Ingen invitasjonstoken funnet. Vennligst bruk lenken fra e-posten.');
      setLoading(false);
      return;
    }
    
    verifyToken();
  }, [token]);
  
  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/api/beta/verify-token?token=${token}`);
      setInvitation(response.data);
      setFormData(prev => ({
        ...prev,
        email: response.data.email,
        name: response.data.name || '',
        company: response.data.company || ''
      }));
    } catch (err: any) {
      const message = err.response?.data?.detail || 'Invitasjonen er ugyldig eller utløpt';
      setError(message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoogleRegister = () => {
    if (!token) return;
    setGoogleLoading(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Store beta_token in localStorage before redirect (query params can cause redirect_uri_mismatch)
    localStorage.setItem('pending_beta_token', token);
    // Using Emergent-managed Google Auth - simple redirect URL without query params
    const redirectUrl = window.location.origin + '/beta-auth-callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  
  const handleMicrosoftRegister = () => {
    if (!token) return;
    setMicrosoftLoading(true);
    // Pass beta token to OAuth flow - MUST use /api prefix for backend routing
    window.location.href = `${API}/api/auth/microsoft/login?beta_token=${token}&redirect=/lexy/dashboard`;
  };
  
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passordene er ikke like');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Passordet må være minst 8 tegn');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/beta/register`, {
        token,
        name: formData.name,
        password: formData.password,
        company: formData.company
      });
      
      toast.success('Konto opprettet! Du kan nå logge inn.');
      navigate('/lexy/login', { 
        state: { email: formData.email, message: 'Konto opprettet! Logg inn med din nye konto.' } 
      });
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Kunne ikke opprette konto');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-violet-500 mx-auto mb-4" />
          <p className="text-slate-400">Verifiserer invitasjon...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900 border border-slate-800 rounded-2xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Invitasjon ikke gyldig</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            Gå til forsiden
          </Button>
        </motion.div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-violet-600/20 text-violet-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Beta-tilgang
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Velkommen til Lexy</h1>
          <p className="text-slate-400">
            Du har {invitation?.trial_days || 30} dager gratis tilgang
          </p>
          {invitation?.email && (
            <p className="text-slate-500 text-sm mt-2">
              Invitert: {invitation.email}
            </p>
          )}
        </div>
        
        {/* Registration options */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8">
          
          {!showEmailForm ? (
            // Social login options
            <div className="space-y-4">
              <p className="text-center text-slate-400 text-sm mb-6">
                Velg hvordan du vil registrere deg
              </p>
              
              {/* Google button */}
              <button
                onClick={handleGoogleRegister}
                disabled={googleLoading}
                className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 text-gray-800 font-medium py-3 px-4 rounded-xl transition-all disabled:opacity-50"
              >
                {googleLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <GoogleLogo />
                    Fortsett med Google
                  </>
                )}
              </button>
              
              {/* Microsoft button */}
              <button
                onClick={handleMicrosoftRegister}
                disabled={microsoftLoading}
                className="w-full flex items-center justify-center gap-3 bg-[#2F2F2F] hover:bg-[#404040] text-white font-medium py-3 px-4 rounded-xl transition-all disabled:opacity-50"
              >
                {microsoftLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <MicrosoftLogo />
                    Fortsett med Microsoft
                  </>
                )}
              </button>
              
              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-700"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-4 bg-slate-900 text-slate-500">eller</span>
                </div>
              </div>
              
              {/* Email option */}
              <button
                onClick={() => setShowEmailForm(true)}
                className="w-full flex items-center justify-center gap-3 bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium py-3 px-4 rounded-xl transition-all border border-slate-700"
              >
                <Mail className="w-5 h-5" />
                Registrer med e-post og passord
              </button>
            </div>
          ) : (
            // Email registration form
            <form onSubmit={handleEmailSubmit} className="space-y-5">
              <button
                type="button"
                onClick={() => setShowEmailForm(false)}
                className="text-sm text-slate-500 hover:text-slate-300 mb-2"
              >
                ← Tilbake til andre alternativer
              </button>
              
              <div>
                <Label className="text-slate-300">E-post</Label>
                <Input
                  type="email"
                  value={formData.email}
                  disabled
                  className="mt-1 bg-slate-800 border-slate-700 text-slate-400"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Fullt navn *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ola Nordmann"
                  className="mt-1 bg-slate-800 border-slate-700 text-white"
                  required
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Firma</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Advokatfirma AS"
                  className="mt-1 bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Passord *</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Minst 8 tegn"
                    className="bg-slate-800 border-slate-700 text-white pr-10"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Bekreft passord *</Label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="Skriv passordet på nytt"
                  className="mt-1 bg-slate-800 border-slate-700 text-white"
                  required
                />
              </div>
              
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-violet-600 hover:bg-violet-700 h-12 text-base"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Opprett konto
                  </>
                )}
              </Button>
            </form>
          )}
          
          <p className="mt-6 text-center text-sm text-slate-500">
            Har du allerede en konto?{' '}
            <a href="/lexy/login" className="text-violet-400 hover:text-violet-300">
              Logg inn
            </a>
          </p>
        </div>
        
        {/* Footer */}
        <p className="mt-6 text-center text-xs text-slate-600">
          Ved å opprette en konto godtar du våre{' '}
          <a href="/terms" className="text-slate-500 hover:text-slate-400">vilkår</a>
          {' '}og{' '}
          <a href="/privacy" className="text-slate-500 hover:text-slate-400">personvern</a>
        </p>
      </motion.div>
    </div>
  );
};

export default BetaRegister;
